import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertCircle, Clock3, Loader2, MessageSquareReply, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { getApiUrl } from "../../config/api";

type TicketStatus = "open" | "in_progress" | "resolved";

interface TicketSummary {
  id: number;
  tenant_id: number;
  requester_id: number;
  subject: string;
  status: TicketStatus;
  school_name: string;
  requester_name: string;
  requester_email: string;
  created_at: string;
  updated_at: string;
}

interface TicketMessage {
  id: number;
  sender_name: string;
  sender_role: string;
  message: string;
  created_at: string;
}

interface TicketDetail extends TicketSummary {}

const STATUS_STYLES: Record<TicketStatus, string> = {
  open: "bg-rose-50 text-rose-700 border-rose-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200"
};

const SupportTickets: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [summary, setSummary] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [selected, setSelected] = useState<TicketDetail | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [reply, setReply] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | TicketStatus>("all");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl("superadmin/support/tickets"), {
        headers,
        params: {
          status: statusFilter === "all" ? undefined : statusFilter,
          q: search.trim() || undefined
        }
      });
      setTickets(response.data?.tickets || []);
      setSummary(response.data?.summary || { total: 0, open: 0, in_progress: 0, resolved: 0 });
    } finally {
      setLoading(false);
    }
  };

  const loadTicket = async (id: number) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(getApiUrl(`superadmin/support/tickets/${id}`), { headers });
      setSelected(response.data?.ticket || null);
      setMessages(response.data?.messages || []);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const handleRefresh = async () => {
    await loadTickets();
    if (selected?.id) {
      await loadTicket(selected.id);
    }
  };

  const updateStatus = async (status: TicketStatus) => {
    if (!selected) return;
    try {
      setSubmitting(true);
      setNotice("");
      await axios.patch(getApiUrl(`superadmin/support/tickets/${selected.id}`), { status }, { headers });
      await loadTickets();
      await loadTicket(selected.id);
      setNotice(`Ticket marked ${status.replace("_", " ")}.`);
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to update ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    try {
      setSubmitting(true);
      setNotice("");
      await axios.post(getApiUrl(`superadmin/support/tickets/${selected.id}/reply`), { message: reply.trim() }, { headers });
      setReply("");
      await loadTicket(selected.id);
      await loadTickets();
      setNotice("Reply sent.");
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to send reply.");
    } finally {
      setSubmitting(false);
    }
  };

  const summaryCards = [
    { label: "All Tickets", value: summary.total, Icon: AlertCircle },
    { label: "Open", value: summary.open, Icon: Clock3 },
    { label: "In Progress", value: summary.in_progress, Icon: Loader2 },
    { label: "Resolved", value: summary.resolved, Icon: ShieldCheck }
  ];

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Support Oversight</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform support tickets</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Review school-submitted support issues, track resolution status, and respond from a single superadmin workspace.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[260px]">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadTickets()}
                  placeholder="Search by school, requester, or subject"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-12 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              >
                <option value="all">All statuses</option>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
              </select>
              <button onClick={handleRefresh} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, Icon }) => (
            <article key={label} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">{label}</p>
                  <p className="mt-3 text-3xl font-black text-slate-900">{value}</p>
                </div>
                <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                  <Icon size={20} />
                </div>
              </div>
            </article>
          ))}
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.95fr_1.45fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-black text-slate-900">Queue</h2>
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{tickets.length} tickets</span>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">Loading tickets...</div>
              ) : tickets.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                  No support tickets matched the current filters.
                </div>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => loadTicket(ticket.id)}
                    className={`w-full rounded-[1.5rem] border p-4 text-left transition ${selected?.id === ticket.id ? "border-teal-200 bg-teal-50/60" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-slate-900">{ticket.subject}</p>
                        <p className="mt-1 truncate text-xs text-slate-500">{ticket.school_name} · {ticket.requester_name}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${STATUS_STYLES[ticket.status]}`}>
                        {ticket.status.replace("_", " ")}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-slate-400">Opened {new Date(ticket.created_at).toLocaleString()}</div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            {!selected ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
                Select a ticket to review the full thread, requester context, and resolution controls.
              </div>
            ) : detailLoading ? (
              <div className="flex min-h-[420px] items-center justify-center rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
                Loading ticket details...
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="text-2xl font-black tracking-tight text-slate-950">{selected.subject}</h2>
                    <p className="mt-2 text-sm text-slate-500">
                      {selected.school_name} · {selected.requester_name} · {selected.requester_email}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">Opened {new Date(selected.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(["open", "in_progress", "resolved"] as TicketStatus[]).map((status) => (
                      <button
                        key={status}
                        onClick={() => updateStatus(status)}
                        disabled={submitting}
                        className={`rounded-2xl border px-4 py-2 text-xs font-black uppercase tracking-[0.18em] ${STATUS_STYLES[status]} disabled:opacity-60`}
                      >
                        {status.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="my-5 max-h-[420px] space-y-3 overflow-y-auto pr-1">
                  {messages.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                      No thread messages have been posted yet.
                    </div>
                  ) : (
                    messages.map((message) => (
                      <article key={message.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-bold text-slate-800">{message.sender_name}</div>
                          <div className="text-xs text-slate-400">{new Date(message.created_at).toLocaleString()}</div>
                        </div>
                        <div className="mt-1 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">{message.sender_role}</div>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{message.message}</p>
                      </article>
                    ))
                  )}
                </div>

                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                  <label className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">Reply</label>
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={4}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-300"
                    placeholder="Add a clear next step, troubleshooting request, or resolution note."
                  />
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-400">Replies are visible in the full support thread for the school.</p>
                    <button
                      onClick={sendReply}
                      disabled={submitting || !reply.trim()}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      <MessageSquareReply size={16} />
                      Send reply
                    </button>
                  </div>
                </div>

                {notice && <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{notice}</div>}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SupportTickets;
